import { useEffect, useId, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import ClaimsFlowMain from './ClaimsFlowMain'
import ClaimsYellowHeader from './ClaimsYellowHeader'
import DashboardNavigation from './DashboardNavigation'
import Footer from './Footer'
import { ChevronRightIcon, FormCard, PrimaryButton, SelectableCard } from './ui'
import type { PolicyLink } from '../services/types'
import { decodePolicyRef } from '../utils/decodePolicyRef'
import ClaimsDesktopProgress from './ClaimsDesktopProgress'
import {
  buildClaimOtherSingleVehicleDescribeHref,
  buildClaimOtherSingleVehicleDriverHref,
  CLAIM_FLOW_PATH,
  formatClaimPolicySummaryLine,
  formatIncidentRailSummary,
  formatMultiVehicleCollisionRailSummary,
  formatMultiVehicleOtherPartyRailSummary,
  formatSingleVehicleCollisionRailSummary,
  OTHER_CLAIM_SUBTYPE_QUERY_KEY,
} from '../claims/claimFlowModel'
import type { SingleVehicleCollisionTargetId } from '../claims/claimDraftStorage'
import {
  isSingleVehicleCollisionTargetGateSatisfied,
  isSingleVehicleDriverDetailsGateSatisfied,
  persistSingleVehicleCollisionTargetPatch,
  readClaimDraft,
} from '../claims/claimDraftStorage'
import { parseOtherClaimSubtypeQuery, type OtherClaimSubtypeId } from '../claims/otherClaimSubtypeOptions'
import {
  claimFormFieldClass,
  CLAIM_FORM_FIELD_ERROR_RING_WRAPPER_CLASS,
  ClaimFormFieldError,
  ClaimFormSubmitSummaryAlert,
  useClaimFormSubmitAttempt,
} from '../claims/claimFormValidationFeedback'

const SINGLE_VEHICLE_SUBTYPE = 'single-vehicle' as const satisfies OtherClaimSubtypeId

const COLLISION_OPTIONS: readonly {
  id: SingleVehicleCollisionTargetId
  headline: string
  caption: string
}[] = [
  { id: 'animal', headline: 'Animal', caption: 'Collision with an animal' },
  { id: 'object', headline: 'Object', caption: 'A stationary object (for example a pole or tree)' },
  {
    id: 'third_party_property',
    headline: 'Third party property',
    caption: "Someone else's property (for example a house or fence)",
  },
  { id: 'other', headline: 'Other', caption: 'Describe what you collided with' },
]

/**
 * Single-vehicle claim: after driver details, capture what the insured vehicle collided with.
 */
export default function ClaimOtherSingleVehicleCollisionWithStep() {
  const navigate = useNavigate()
  const location = useLocation()
  const headingId = useId()
  const otherDetailsId = useId()

  const queryParams = new URLSearchParams(location.search)
  const policyRef = decodePolicyRef(queryParams.get('policyRef'))
  const typeInQuery = queryParams.get('type')
  const claimSubtype =
    parseOtherClaimSubtypeQuery(queryParams.get(OTHER_CLAIM_SUBTYPE_QUERY_KEY)) ??
    parseOtherClaimSubtypeQuery(readClaimDraft()?.otherClaimSubtypeId ?? null)
  const navState = location.state as { policy?: PolicyLink; preserveScroll?: boolean } | null

  const [selected, setSelected] = useState<SingleVehicleCollisionTargetId | null>(
    () => readClaimDraft()?.singleVehicleCollisionTarget ?? null
  )
  const [otherText, setOtherText] = useState(() => readClaimDraft()?.singleVehicleCollisionTargetOtherText ?? '')

  useEffect(() => {
    if (!navState?.preserveScroll) {
      window.scrollTo(0, 0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (claimSubtype !== SINGLE_VEHICLE_SUBTYPE) return
    const d = readClaimDraft()
    if (!isSingleVehicleDriverDetailsGateSatisfied(d)) {
      navigate(buildClaimOtherSingleVehicleDriverHref(policyRef, typeInQuery, claimSubtype), {
        replace: true,
        state: navState?.policy ? { policy: navState.policy } : undefined,
      })
    }
  }, [claimSubtype, navigate, navState?.policy, policyRef, typeInQuery])

  useEffect(() => {
    if (claimSubtype !== null) return
    const p = new URLSearchParams()
    if (policyRef) p.set('policyRef', policyRef)
    if (typeInQuery) p.set('type', typeInQuery)
    const q = p.toString()
    const path = q ? `${CLAIM_FLOW_PATH.otherClaimSubtype}?${q}` : CLAIM_FLOW_PATH.otherClaimSubtype
    navigate(path, {
      replace: true,
      state: navState?.policy ? { policy: navState.policy } : undefined,
    })
  }, [claimSubtype, navigate, navState?.policy, policyRef, typeInQuery])

  useEffect(() => {
    const t = window.setTimeout(() => {
      const trimmedOther = otherText.trim().slice(0, 500)
      persistSingleVehicleCollisionTargetPatch(policyRef, {
        singleVehicleCollisionTarget: selected,
        singleVehicleCollisionTargetOtherText:
          selected === 'other' ? (trimmedOther.length > 0 ? trimmedOther : null) : null,
      })
    }, 300)
    return () => window.clearTimeout(t)
  }, [policyRef, selected, otherText])

  const policySummary = formatClaimPolicySummaryLine(
    navState?.policy ?? (policyRef ? { policyRef } : null)
  )
  const draft = readClaimDraft()

  const canProceed =
    claimSubtype === SINGLE_VEHICLE_SUBTYPE &&
    draft != null &&
    isSingleVehicleCollisionTargetGateSatisfied({
      ...draft,
      singleVehicleCollisionTarget: selected,
      singleVehicleCollisionTargetOtherText: selected === 'other' ? otherText : null,
    })

  const { submitAttempted, showSubmitSummary, attemptSubmit, clearSubmitAttempt } =
    useClaimFormSubmitAttempt(canProceed)
  const selectionError = submitAttempted && selected === null
  const otherTextError = submitAttempted && selected === 'other' && otherText.trim().length === 0

  const backToDriver = () => {
    if (claimSubtype === null) return
    navigate(buildClaimOtherSingleVehicleDriverHref(policyRef, typeInQuery, claimSubtype), {
      state: navState?.policy ? { policy: navState.policy } : undefined,
    })
  }

  const handleNext = () => {
    attemptSubmit(() => {
      if (claimSubtype === null) return
      const trimmedOther = otherText.trim().slice(0, 500)
      persistSingleVehicleCollisionTargetPatch(policyRef, {
        singleVehicleCollisionTarget: selected,
        singleVehicleCollisionTargetOtherText: selected === 'other' ? trimmedOther : null,
      })
      const path = buildClaimOtherSingleVehicleDescribeHref(policyRef, typeInQuery, claimSubtype)
      navigate(path, {
        state: navState?.policy ? { policy: navState.policy } : undefined,
      })
    })
  }

  const inputClass =
    'w-full rounded-xl border border-solid border-border-primary bg-background-primary px-[14px] py-[12px] font-body text-sm leading-sm text-text-primary-900 shadow-xs placeholder:text-text-placeholder focus:outline-none focus-visible:ring-2 focus-visible:ring-foreground-brand-solid focus-visible:ring-offset-2'

  if (claimSubtype === null || claimSubtype !== SINGLE_VEHICLE_SUBTYPE) {
    return null
  }

  return (
    <div className="relative flex min-h-screen w-full flex-col items-start bg-background-secondary">
      <DashboardNavigation />

      <ClaimsYellowHeader
        backAriaLabel="Back"
        onBack={backToDriver}
        title="What you collided with"
      />

      <ClaimsFlowMain
        aside={
          <ClaimsDesktopProgress
            policySummary={policySummary}
            claimEntryBranch="other"
            otherClaimSubtypeId={claimSubtype}
            incidentSummary={formatIncidentRailSummary(draft)}
            multiVehicleCollisionSummary={formatMultiVehicleCollisionRailSummary(draft ?? undefined)}
            multiVehicleOtherPartySummary={formatMultiVehicleOtherPartyRailSummary(draft ?? undefined)}
            multiVehicleIncidentDescription={draft?.multiVehicleIncidentDescription ?? null}
            multiVehicleDamageDescription={draft?.multiVehicleDamageDescription ?? null}
            multiVehicleClaimDescription={draft?.multiVehicleClaimDescription ?? null}
            singleVehicleCollisionSummary={formatSingleVehicleCollisionRailSummary(draft ?? undefined)}
            singleVehicleIncidentDescription={draft?.singleVehicleIncidentDescription ?? null}
            singleVehicleDamageDescription={draft?.singleVehicleDamageDescription ?? null}
            windscreenGstRegistered={draft?.windscreenGstRegistered ?? null}
            windscreenAbn={draft?.windscreenAbn ?? null}
            windscreenItcEntitled={draft?.windscreenItcEntitled ?? null}
            windscreenItcPercentage={draft?.windscreenItcPercentage ?? null}
          />
        }
      >
        <FormCard>
          <section className="flex w-full flex-col gap-[20px]" aria-labelledby={headingId}>
            {showSubmitSummary ? <ClaimFormSubmitSummaryAlert /> : null}
            <h2
              id={headingId}
              className="m-0 font-display font-semibold text-display-xs leading-display-xs text-text-primary-900"
            >
              What did your vehicle collide with?
            </h2>

            <ul
              className={`m-0 flex w-full list-none flex-col gap-[12px] p-0 md:gap-[16px] ${selectionError ? CLAIM_FORM_FIELD_ERROR_RING_WRAPPER_CLASS : ''}`}
              aria-label="Collision type"
            >
              {COLLISION_OPTIONS.map((opt) => (
                <li key={opt.id} className="w-full">
                  <SelectableCard
                    prominent
                    selected={selected === opt.id}
                    headline={opt.headline}
                    caption={opt.caption}
                    onClick={() => {
                      clearSubmitAttempt()
                      setSelected(opt.id)
                    }}
                  />
                </li>
              ))}
            </ul>
            {selectionError ? (
              <ClaimFormFieldError id={`${headingId}-selection-error`}>
                Select what your vehicle collided with to continue.
              </ClaimFormFieldError>
            ) : null}

            {selected === 'other' ? (
              <div className="flex w-full flex-col gap-[8px]">
                <label htmlFor={otherDetailsId} className="font-body text-sm leading-sm text-text-tertiary-600">
                  Please describe
                </label>
                <textarea
                  id={otherDetailsId}
                  rows={4}
                  value={otherText}
                  onChange={(e) => {
                    clearSubmitAttempt()
                    setOtherText(e.target.value.slice(0, 500))
                  }}
                  aria-invalid={otherTextError}
                  aria-describedby={otherTextError ? `${otherDetailsId}-error` : undefined}
                  className={claimFormFieldClass(inputClass, otherTextError)}
                  placeholder="Tell us briefly what you collided with"
                />
                {otherTextError ? (
                  <ClaimFormFieldError id={`${otherDetailsId}-error`}>
                    Describe what you collided with to continue.
                  </ClaimFormFieldError>
                ) : null}
              </div>
            ) : null}
          </section>
        </FormCard>

        <div className="sticky bottom-0 w-full bg-background-secondary pb-[8px] pt-[8px] md:static md:py-0">
          <PrimaryButton
            type="button"
            onClick={handleNext}
            size="lg"
            fullWidth
            rightIcon={
              <div className="size-5 text-text-white">
                <ChevronRightIcon />
              </div>
            }
            aria-label="Next"
          >
            Next
          </PrimaryButton>
        </div>
      </ClaimsFlowMain>

      <Footer />
    </div>
  )
}
